/**
 * ManagePlayersPanel — Assign party characters to controllers for co-op.
 *
 * Layout (desktop/tablet landscape):
 *   [MANAGE PLAYERS]                    [Close X]
 *   ─────────────────────────────────────────────
 *   Player 1 (WASD + Mouse)
 *   Character: [Dropdown ▼]             [Navigator]
 *   ...× 4 rows...
 *   ─────────────────────────────────────────────
 *   [Auto-Assign]                       [Confirm]
 *
 * On mobile portrait each player row stacks into a single-column card.
 *
 * Gracefully degrades when CoopSystem is not yet loaded — assignments
 * are stored in a local map and Auto-Assign does a simple random shuffle.
 *
 * Opened/toggled via Tab key (see main.js) or window.__managePlayersPanel.
 *
 * @module ManagePlayersPanel
 */

import { UIPanel } from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';

// ─── Style injection ──────────────────────────────────────────────────────────

const STYLE_ID = 'spellroads-manage-players-style';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* ══════════════════════════════════════════════════════════════
       ManagePlayersPanel — Spellroads
       ══════════════════════════════════════════════════════════════ */

    #panel-manage-players {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: min(580px, 96vw);
      max-height: min(700px, 92vh);
      z-index: 222;
      display: none;
      flex-direction: column;
      background: rgba(8, 6, 4, 0.98);
      border: 1px solid rgba(200, 160, 32, 0.35);
      border-radius: 6px;
      box-shadow: 0 8px 40px rgba(0,0,0,0.8);
      pointer-events: all;
      overflow: hidden;
      font-family: 'Georgia', 'Times New Roman', serif;
      color: #e8d5b0;
    }

    #panel-manage-players.visible {
      display: flex;
    }

    /* ── Header ──────────────────────────────────────────────────── */
    .mp-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 20px);
      background: rgba(0, 0, 0, 0.55);
      border-bottom: 1px solid rgba(200, 160, 32, 0.2);
      flex-shrink: 0;
    }

    .mp-title {
      font-size: clamp(0.85rem, 1.6vw, 1.1rem);
      font-weight: 700;
      color: #c8a020;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .mp-close {
      background: none;
      border: 1px solid rgba(200, 160, 32, 0.25);
      color: #a09070;
      cursor: pointer;
      width: 44px;
      height: 44px;
      min-width: 44px;
      min-height: 44px;
      border-radius: 4px;
      font-size: 1.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s, border-color 0.15s, background 0.15s;
      flex-shrink: 0;
    }

    .mp-close:hover,
    .mp-close:focus-visible {
      color: #c8a020;
      border-color: #c8a020;
      background: rgba(200, 160, 32, 0.08);
      outline: none;
    }

    /* ── Body ────────────────────────────────────────────────────── */
    .mp-body {
      flex: 1;
      overflow-y: auto;
      padding: clamp(10px, 1.5vh, 18px) clamp(12px, 2vw, 20px);
      display: flex;
      flex-direction: column;
      gap: clamp(8px, 1.5vh, 12px);
    }

    /* ── Loading state ───────────────────────────────────────────── */
    .mp-loading {
      font-size: clamp(0.78rem, 1.2vw, 0.9rem);
      color: #706050;
      font-style: italic;
      padding: 8px 0 4px;
    }

    /* ── Player row / card ───────────────────────────────────────── */
    .mp-player-card {
      display: flex;
      align-items: center;
      gap: clamp(8px, 1.5vw, 16px);
      padding: clamp(10px, 1.5vh, 14px) clamp(10px, 1.5vw, 14px);
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(90, 74, 53, 0.4);
      border-radius: 5px;
      transition: border-color 0.15s;
    }

    .mp-player-card:focus-within {
      border-color: rgba(200, 160, 32, 0.35);
    }

    /* Left column: player label */
    .mp-player-info {
      flex: 0 0 auto;
      width: clamp(130px, 22vw, 170px);
    }

    .mp-player-number {
      font-size: clamp(0.78rem, 1.2vw, 0.9rem);
      font-weight: 700;
      color: #c8b888;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 2px;
    }

    .mp-player-input-label {
      font-size: clamp(0.62rem, 0.9vw, 0.72rem);
      color: #706050;
    }

    /* Center column: character dropdown */
    .mp-char-select-wrap {
      flex: 1;
      min-width: 0;
    }

    .mp-char-select {
      width: 100%;
      background: rgba(26, 21, 16, 0.9);
      border: 1px solid rgba(90, 74, 53, 0.6);
      border-radius: 4px;
      color: #e8d5b0;
      font-family: inherit;
      font-size: clamp(0.72rem, 1.1vw, 0.82rem);
      padding: 6px 10px;
      min-height: 44px;
      cursor: pointer;
      appearance: auto;
      transition: border-color 0.15s;
    }

    .mp-char-select:hover,
    .mp-char-select:focus-visible {
      border-color: #c8a020;
      outline: none;
    }

    .mp-char-select option {
      background: #1a1510;
      color: #e8d5b0;
    }

    /* Right column: Navigator radio button */
    .mp-navigator-wrap {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
    }

    .mp-navigator-label {
      font-size: clamp(0.6rem, 0.85vw, 0.7rem);
      color: #706050;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      white-space: nowrap;
    }

    .mp-navigator-btn {
      width: 44px;
      height: 44px;
      min-width: 44px;
      min-height: 44px;
      background: rgba(26, 21, 16, 0.8);
      border: 2px solid rgba(90, 74, 53, 0.5);
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
      color: #706050;
    }

    .mp-navigator-btn:hover,
    .mp-navigator-btn:focus-visible {
      border-color: #c8a020;
      background: rgba(200, 160, 32, 0.08);
      outline: none;
    }

    .mp-navigator-btn.is-navigator {
      border-color: #c8a020;
      background: rgba(200, 160, 32, 0.15);
      box-shadow: 0 0 8px rgba(200, 160, 32, 0.35);
      color: #c8a020;
    }

    .mp-navigator-btn:disabled {
      opacity: 0.35;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* ── Inline error message ────────────────────────────────────── */
    .mp-error {
      font-size: clamp(0.65rem, 0.9vw, 0.75rem);
      color: #c83020;
      padding: 2px 0 0 2px;
      min-height: 1.2em;
    }

    /* ── Footer ──────────────────────────────────────────────────── */
    .mp-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: clamp(10px, 1.5vh, 14px) clamp(12px, 2vw, 20px);
      background: rgba(0, 0, 0, 0.4);
      border-top: 1px solid rgba(90, 74, 53, 0.3);
      flex-shrink: 0;
    }

    .mp-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 44px;
      min-width: 44px;
      padding: 8px clamp(14px, 2vw, 22px);
      background: rgba(42, 32, 20, 0.9);
      border: 1px solid rgba(90, 74, 53, 0.7);
      border-radius: 4px;
      color: #c8b888;
      font-family: inherit;
      font-size: clamp(0.7rem, 1.1vw, 0.8rem);
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }

    .mp-btn:hover,
    .mp-btn:focus-visible {
      background: rgba(60, 46, 28, 0.95);
      border-color: #c8a020;
      color: #e8d5b0;
      outline: none;
    }

    .mp-btn:active {
      transform: translateY(1px);
    }

    .mp-btn--primary {
      background: rgba(200, 160, 32, 0.15);
      border-color: rgba(200, 160, 32, 0.7);
      color: #e8c870;
    }

    .mp-btn--primary:hover,
    .mp-btn--primary:focus-visible {
      background: rgba(200, 160, 32, 0.28);
      border-color: #c8a020;
      color: #e8d060;
    }

    /* ── Responsive: mobile portrait — stack each card ───────────── */
    @media (max-width: 767px) and (orientation: portrait) {
      #panel-manage-players {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        transform: none;
        width: 100%;
        max-height: 100%;
        border-radius: 0;
        border-left: none;
        border-right: none;
        border-top: none;
      }

      .mp-player-card {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
      }

      .mp-player-info {
        width: 100%;
      }

      .mp-char-select-wrap {
        width: 100%;
      }

      .mp-navigator-wrap {
        flex-direction: row;
        align-items: center;
        gap: 10px;
        width: 100%;
      }

      .mp-navigator-label {
        font-size: 0.75rem;
      }
    }

    /* ── Responsive: mobile landscape ───────────────────────────── */
    @media (max-width: 1023px) and (orientation: landscape) {
      #panel-manage-players {
        width: min(640px, 98vw);
        max-height: 90vh;
      }

      .mp-player-info {
        width: clamp(110px, 18vw, 140px);
      }
    }
  `;
  document.head.appendChild(style);
}

// ─── Player configuration ─────────────────────────────────────────────────────

/** Static input-label text per player index (0-based). */
const PLAYER_INPUT_LABELS = [
  'WASD + Mouse',
  'Arrows + Gamepad 2',
  'Gamepad 3',
  'Gamepad 4',
];

// ─── Panel class ──────────────────────────────────────────────────────────────

class ManagePlayersPanelClass extends UIPanel {
  constructor() {
    super('panel-manage-players', {
      trapFocus: true,
      zLayer: 'modal',
    });

    /**
     * Working copy of assignments: playerIndex (0-based) => characterId | null.
     * @type {Map<number, string|null>}
     */
    this._assignments = new Map([
      [0, null],
      [1, null],
      [2, null],
      [3, null],
    ]);

    /** Navigator player index (0-based), or null if none assigned. */
    this._navigatorIndex = null;

    /** Cached party characters from CharacterSystem (or placeholders). */
    this._partyChars = [];

    /** Whether CoopSystem was found on last open. */
    this._coopLoaded = false;

    this._panelInitialized = false;
  }

  // ─── Init ────────────────────────────────────────────────────────────────

  init() {
    if (this._panelInitialized) return;
    this._panelInitialized = true;
    injectStyles();
    DebugLog.info('[ui] ManagePlayersPanel initialized');
  }

  // ─── UIPanel overrides ───────────────────────────────────────────────────

  /**
   * Provide initial HTML skeleton that UIPanel._create() will use.
   * The body content is replaced each time the panel opens via updateContent().
   * @returns {string}
   */
  _buildHTML() {
    return `
      <div class="mp-header">
        <span class="mp-title">Manage Players</span>
        <button class="mp-close" aria-label="Close panel" tabindex="0">&#x2715;</button>
      </div>
      <div class="mp-body" id="mp-body-inner">
        <p class="mp-loading">Loading...</p>
      </div>
      <div class="mp-footer">
        <button class="mp-btn" id="mp-btn-auto" tabindex="0">Auto-Assign</button>
        <button class="mp-btn mp-btn--primary" id="mp-btn-confirm" tabindex="0">Confirm</button>
      </div>
    `;
  }

  /**
   * Called by UIPanel.show() every time the panel is opened.
   * Syncs from CoopSystem, loads characters, rebuilds body.
   */
  updateContent() {
    this._syncFromCoopSystem();
    this._loadPartyChars();
    this._renderBody();
    this._bindFooterButtons();
    this._bindCloseButton();
    this._refreshFocusables();
  }

  // ─── Data helpers ────────────────────────────────────────────────────────

  /**
   * Pull current assignments from CoopSystem if available.
   * Keeps this._assignments intact if CoopSystem is not found.
   */
  _syncFromCoopSystem() {
    const cs = this._getCoopSystem();
    if (!cs) {
      this._coopLoaded = false;
      return;
    }

    this._coopLoaded = true;

    try {
      const existing = typeof cs.getAssignments === 'function' ? cs.getAssignments() : null;
      if (existing) {
        const entries = existing instanceof Map
          ? existing.entries()
          : Object.entries(existing);
        for (const [k, v] of entries) {
          const idx = parseInt(k, 10);
          if (!Number.isNaN(idx) && idx >= 0 && idx < 4) {
            this._assignments.set(idx, v ?? null);
          }
        }
      }
    } catch (err) {
      DebugLog.warn(`[ui] ManagePlayersPanel: getAssignments error — ${err.message}`);
    }

    try {
      if (typeof cs.getNavigatorIndex === 'function') {
        const nav = cs.getNavigatorIndex();
        this._navigatorIndex = typeof nav === 'number' ? nav : null;
      }
    } catch (err) {
      DebugLog.warn(`[ui] ManagePlayersPanel: getNavigatorIndex error — ${err.message}`);
    }
  }

  /**
   * Load party characters. Falls back to placeholder data when CharacterSystem
   * is unavailable so the panel renders correctly for testing.
   */
  _loadPartyChars() {
    try {
      const CharSys = window.__characterSystem ?? null;
      if (CharSys && typeof CharSys.getAll === 'function') {
        this._partyChars = CharSys.getAll().filter(c => c.isHero).slice(0, 4);
        return;
      }
    } catch (err) {
      DebugLog.warn(`[ui] ManagePlayersPanel: CharacterSystem load error — ${err.message}`);
    }

    // Fallback placeholder list for pre-data milestones
    this._partyChars = [
      { id: 'char_sigrid', name: 'Sigrid Flamehair' },
      { id: 'char_bjorn',  name: 'Bjorn Ironshield'  },
      { id: 'char_astrid', name: 'Astrid Skywarden'  },
      { id: 'char_ragnar', name: 'Ragnar Coldaxe'    },
    ];
  }

  /**
   * @returns {object|null} CoopSystem singleton exposed on window, or null.
   */
  _getCoopSystem() {
    return window.__coopSystem ?? null;
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  _renderBody() {
    const bodyEl = this.el.querySelector('#mp-body-inner');
    if (!bodyEl) return;

    const rowsHTML = [0, 1, 2, 3].map(i => this._buildPlayerRowHTML(i)).join('');
    bodyEl.innerHTML = rowsHTML
      + (!this._coopLoaded
        ? `<p class="mp-loading">Co-op system loading... Auto-Assign uses random order.</p>`
        : '');

    this._bindPlayerRows();
  }

  /**
   * Build the HTML for one player row.
   * @param {number} playerIndex — 0-based
   * @returns {string}
   */
  _buildPlayerRowHTML(playerIndex) {
    const playerNum    = playerIndex + 1;
    const inputLabel   = PLAYER_INPUT_LABELS[playerIndex] ?? `Gamepad ${playerNum}`;
    const assignedId   = this._assignments.get(playerIndex) ?? null;
    const isNavigator  = this._navigatorIndex === playerIndex;
    const hasCharacter = Boolean(assignedId);

    const noneOption = `<option value="" ${!assignedId ? 'selected' : ''}>None</option>`;
    const charOptions = this._partyChars.map(c => {
      const sel = assignedId === c.id ? 'selected' : '';
      return `<option value="${this._esc(c.id)}" ${sel}>${this._esc(c.name)}</option>`;
    }).join('');

    return `
      <div class="mp-player-card" data-player-index="${playerIndex}">
        <div class="mp-player-info">
          <div class="mp-player-number">Player ${playerNum}</div>
          <div class="mp-player-input-label">${this._esc(inputLabel)}</div>
        </div>

        <div class="mp-char-select-wrap">
          <select
            class="mp-char-select"
            id="mp-select-p${playerIndex}"
            data-player-index="${playerIndex}"
            aria-label="Character for Player ${playerNum}"
            tabindex="0"
          >${noneOption}${charOptions}</select>
          <div class="mp-error" id="mp-error-p${playerIndex}" aria-live="polite" role="alert"></div>
        </div>

        <div class="mp-navigator-wrap">
          <div class="mp-navigator-label">Navigator</div>
          <button
            class="mp-navigator-btn${isNavigator ? ' is-navigator' : ''}"
            id="mp-nav-p${playerIndex}"
            data-player-index="${playerIndex}"
            aria-label="Set Player ${playerNum} as Navigator"
            aria-pressed="${isNavigator}"
            ${hasCharacter ? '' : 'disabled'}
            tabindex="0"
          >&#9906;</button>
        </div>
      </div>
    `;
  }

  // ─── Event binding ────────────────────────────────────────────────────────

  _bindPlayerRows() {
    this.el.querySelectorAll('.mp-char-select').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const idx   = parseInt(e.target.dataset.playerIndex, 10);
        const charId = e.target.value || null;
        this._handleCharacterChange(idx, charId);
      });
    });

    this.el.querySelectorAll('.mp-navigator-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt(e.currentTarget.dataset.playerIndex, 10);
        this._handleNavigatorToggle(idx);
      });
    });
  }

  _bindFooterButtons() {
    const autoBtn = this.el.querySelector('#mp-btn-auto');
    if (autoBtn) autoBtn.addEventListener('click', () => this._handleAutoAssign());

    const confirmBtn = this.el.querySelector('#mp-btn-confirm');
    if (confirmBtn) confirmBtn.addEventListener('click', () => this._handleConfirm());
  }

  _bindCloseButton() {
    const closeBtn = this.el.querySelector('.mp-close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.hide());
  }

  // ─── Interaction handlers ─────────────────────────────────────────────────

  /**
   * Handle character selection from a dropdown.
   * Blocks duplicate assignments and shows an inline error if attempted.
   * @param {number} playerIndex
   * @param {string|null} charId
   */
  _handleCharacterChange(playerIndex, charId) {
    // Guard: reject duplicate assignments
    if (charId) {
      for (const [otherIdx, existingId] of this._assignments) {
        if (otherIdx !== playerIndex && existingId === charId) {
          this._showError(playerIndex, 'Character already assigned to another player.');
          // Revert the select element visually
          const sel = this.el.querySelector(`#mp-select-p${playerIndex}`);
          if (sel) sel.value = this._assignments.get(playerIndex) ?? '';
          return;
        }
      }
    }

    this._clearError(playerIndex);
    this._assignments.set(playerIndex, charId);

    // If the player lost their character and was Navigator, clear Navigator
    if (!charId && this._navigatorIndex === playerIndex) {
      this._navigatorIndex = null;
      this._refreshNavigatorButtons();
    }

    // Update Navigator button enabled state
    const navBtn = this.el.querySelector(`#mp-nav-p${playerIndex}`);
    if (navBtn) {
      if (charId) {
        navBtn.removeAttribute('disabled');
      } else {
        navBtn.setAttribute('disabled', '');
        navBtn.classList.remove('is-navigator');
        navBtn.setAttribute('aria-pressed', 'false');
      }
    }

    DebugLog.info(`[ui] ManagePlayersPanel: P${playerIndex + 1} char="${charId ?? 'None'}"`);
  }

  /**
   * Toggle the Navigator designation for a player.
   * Selecting a new Navigator automatically deselects the previous one.
   * @param {number} playerIndex
   */
  _handleNavigatorToggle(playerIndex) {
    if (!this._assignments.get(playerIndex)) return;

    // Clicking the current Navigator deselects; clicking another reassigns
    this._navigatorIndex = this._navigatorIndex === playerIndex ? null : playerIndex;
    this._refreshNavigatorButtons();

    const label = this._navigatorIndex != null ? `P${this._navigatorIndex + 1}` : 'None';
    DebugLog.info(`[ui] ManagePlayersPanel: Navigator => ${label}`);
  }

  /**
   * Auto-assign characters to players.
   * Delegates to CoopSystem.autoAssign() when available; falls back to a
   * simple randomised sequential assignment.
   */
  _handleAutoAssign() {
    const cs = this._getCoopSystem();

    if (cs && typeof cs.autoAssign === 'function') {
      try {
        cs.autoAssign();
        this._syncFromCoopSystem();
        DebugLog.info('[ui] ManagePlayersPanel: CoopSystem.autoAssign() called');
      } catch (err) {
        DebugLog.warn(`[ui] ManagePlayersPanel: CoopSystem.autoAssign error — ${err.message}`);
        this._fallbackAutoAssign();
      }
    } else {
      this._fallbackAutoAssign();
    }

    this._renderBody();
    this._bindFooterButtons();
    this._bindCloseButton();
    this._refreshFocusables();
  }

  /**
   * Fallback auto-assign: randomly shuffle party characters across players.
   * Sets P1 (or first available) as Navigator.
   */
  _fallbackAutoAssign() {
    const chars = [...this._partyChars];
    // Fisher-Yates shuffle
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }

    this._assignments.clear();
    for (let i = 0; i < 4; i++) {
      this._assignments.set(i, chars[i]?.id ?? null);
    }

    // Assign Navigator to the first player who has a character
    this._navigatorIndex = null;
    for (let i = 0; i < 4; i++) {
      if (this._assignments.get(i)) {
        this._navigatorIndex = i;
        break;
      }
    }

    DebugLog.info('[ui] ManagePlayersPanel: fallback auto-assign applied');
  }

  /**
   * Validate assignments, save to CoopSystem, emit confirmation event, close.
   */
  _handleConfirm() {
    // Clear any stale errors
    for (let i = 0; i < 4; i++) this._clearError(i);

    // Validate for duplicates (belt-and-suspenders — should not be possible
    // via normal UI, but protects against programmatic manipulation)
    const seen = new Set();
    let hasError = false;
    for (const [idx, charId] of this._assignments) {
      if (charId && seen.has(charId)) {
        this._showError(idx, 'Duplicate character assignment.');
        hasError = true;
      } else if (charId) {
        seen.add(charId);
      }
    }
    if (hasError) return;

    // Persist to CoopSystem
    const cs = this._getCoopSystem();
    if (cs) {
      try {
        if (typeof cs.assignCharacter === 'function') {
          for (const [playerIndex, charId] of this._assignments) {
            cs.assignCharacter(playerIndex, charId);
          }
        }
        if (this._navigatorIndex != null && typeof cs.setNavigator === 'function') {
          cs.setNavigator(this._navigatorIndex);
        }
        DebugLog.info('[ui] ManagePlayersPanel: assignments saved to CoopSystem');
      } catch (err) {
        DebugLog.warn(`[ui] ManagePlayersPanel: CoopSystem save error — ${err.message}`);
      }
    }

    // Broadcast confirmed assignments
    try {
      EventBus.emit('coop:assignments_confirmed', {
        assignments: Object.fromEntries(this._assignments),
        navigatorIndex: this._navigatorIndex,
      });
    } catch (_) { /* EventBus might not be available in all test contexts */ }

    DebugLog.info('[ui] ManagePlayersPanel: confirmed, closing');
    this.hide();
  }

  // ─── UI helpers ──────────────────────────────────────────────────────────

  /**
   * Redraw all Navigator buttons to match this._navigatorIndex.
   * Used for lightweight in-place updates (no full body rebuild needed).
   */
  _refreshNavigatorButtons() {
    for (let i = 0; i < 4; i++) {
      const btn = this.el.querySelector(`#mp-nav-p${i}`);
      if (!btn) continue;
      const isNav = this._navigatorIndex === i;
      btn.classList.toggle('is-navigator', isNav);
      btn.setAttribute('aria-pressed', String(isNav));
    }
  }

  /**
   * @param {number} playerIndex
   * @param {string} message
   */
  _showError(playerIndex, message) {
    const el = this.el.querySelector(`#mp-error-p${playerIndex}`);
    if (el) el.textContent = message;
  }

  /**
   * @param {number} playerIndex
   */
  _clearError(playerIndex) {
    const el = this.el.querySelector(`#mp-error-p${playerIndex}`);
    if (el) el.textContent = '';
  }

  /**
   * HTML-escape a value for safe injection into innerHTML.
   * @param {unknown} val
   * @returns {string}
   */
  _esc(val) {
    return String(val ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

// ─── Singleton facade ─────────────────────────────────────────────────────────

/** @type {ManagePlayersPanelClass|null} */
let _instance = null;

const ManagePlayersPanel = {
  /**
   * Create the panel and inject styles.
   * Safe to call multiple times — idempotent.
   */
  init() {
    if (!_instance) {
      _instance = new ManagePlayersPanelClass();
    }
    _instance.init();
  },

  /** Show the panel, optionally passing data. */
  show(data) {
    _instance?.show(data);
  },

  /** Hide the panel. */
  hide() {
    _instance?.hide();
  },

  /**
   * Toggle visibility.
   * @param {*} [data]
   */
  toggle(data) {
    _instance?.toggle(data);
  },

  /** @returns {boolean} */
  isVisible() {
    return _instance?.isVisible() ?? false;
  },

  /** Direct access to the underlying UIPanel instance (for tests). */
  get panel() {
    return _instance;
  },
};

export default ManagePlayersPanel;
